import React, { useMemo, useState, useEffect, useRef } from "react";
import LOCATIONS from "@/data/indianLocations";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

/**
 * Reusable searchable Indian-location combobox.
 * value = { city, state, label } | null
 * onChange = (value) => void
 */
export default function LocationCombobox({ value, onChange, placeholder = "Search a city or state…", testId = "location-combobox" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef();

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const src = LOCATIONS;  // already alphabetical
    if (!query) return src.slice(0, 200);
    return src.filter((loc) =>
      loc.city.toLowerCase().includes(query) ||
      loc.state.toLowerCase().includes(query),
    ).slice(0, 200);
  }, [q]);

  const label = value?.label || "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="w-full flex items-center justify-between border border-slate-200 rounded-md px-3 py-2 text-sm bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10">
          <span className={label ? "text-slate-900" : "text-slate-400"}>{label || placeholder}</span>
          <div className="flex items-center gap-1">
            {value && (
              <span onClick={(e) => { e.stopPropagation(); onChange(null); }}
                className="p-0.5 rounded hover:bg-slate-100" data-testid={`${testId}-clear`}>
                <X className="w-3.5 h-3.5 text-slate-500" />
              </span>
            )}
            <ChevronDown className="w-4 h-4 text-slate-500" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] max-w-md" data-testid={`${testId}-menu`}>
        <div className="p-2 border-b border-slate-100 flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Type to search" className="border-0 shadow-none focus-visible:ring-0 h-8"
            data-testid={`${testId}-input`} />
        </div>
        <ul className="max-h-72 overflow-y-auto py-1" role="listbox">
          {filtered.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-slate-500">No matches</li>
          )}
          {filtered.map((loc) => {
            const isSel = value && value.city === loc.city && value.state === loc.state;
            return (
              <li key={loc.label}>
                <button type="button" onClick={() => { onChange(loc); setOpen(false); setQ(""); }}
                  data-testid={`${testId}-option-${loc.city.replace(/\s+/g, "-")}`}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left hover:bg-slate-50 ${
                    isSel ? "bg-slate-100" : ""
                  }`}>
                  <div>
                    <div className="text-slate-900">{loc.city}</div>
                    <div className="text-xs text-slate-500">{loc.state}</div>
                  </div>
                  {isSel && <Check className="w-4 h-4 text-slate-700" />}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
